import { makeAutoObservable, observable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { userManager } from '@/Core/Managers'

const FAVORITES_URL = '/services/user/favorites'

/**
 * Скільки записів беремо одним запитом — стеля `limit` колекції на беку.
 *
 * Обране читається цілком, а не сторінками: зірочка в будь-якій картці має
 * знати відповідь одразу, без запиту на кожне відкриття, — а на цьому стенді
 * запит коштує секунду воркера.
 */
const LIMIT = 300

/** Ядровий клієнт тримає GET у кеші пів хвилини — обране після зміни мусить бути свіжим. */
const NO_CACHE = { isDisableResponseCache: true }

/** Запис обраного так, як його віддає `/services/user/favorites`. */
export interface Favorite {
    uuid: string
    /** Назва запису на момент, коли його позначили. */
    title: string
    /** Ключ ресурсу фронту: `Setting.User`… */
    entityCode: string
    entityUuid: string
    /** Коментар до позначки (ADR 029): навіщо запис тут. */
    comment?: string | null
}

interface FavoriteListResponse {
    items?: Favorite[]
}

/** Хто зараз увійшов — обране належить акаунту. */
const currentUser = (): string => String(userManager.user['uuid'] ?? userManager.user.id ?? '')

const keyOf = (item: Pick<Favorite, 'entityCode' | 'entityUuid'>): string => `${item.entityCode}:${item.entityUuid}`

/**
 * Обране користувача: один список на застосунок.
 *
 * Читають його двоє — зірочка в шапці картки й шухляда «Обране» в шапці
 * сторінки. Спільний стор потрібен, щоб вони не розходилися: зірочка, знята в
 * картці, відкритій із шухляди, прибирає рядок і з шухляди.
 *
 * Зміни оптимістичні: зірочка заповнюється одразу, а не через секунду
 * відповіді. Якщо запит не вдався, список перечитується — і лишається те, що
 * насправді на сервері.
 */
class FavoritesManager {
    items: Favorite[] = []

    /** Перше читання для цього користувача — шухляда показує скелет. */
    isLoading = false

    /** Записи, для яких запит ще в польоті: зірочка тим часом не клікається. */
    private pending: string[] = []

    /**
     * Для кого список уже прочитано.
     *
     * ⚠️ Саме «для кого», а не прапорець: після виходу й входу іншим акаунтом
     * без перезавантаження сторінки інакше лишилися б зірочки попереднього.
     */
    private loadedFor: string | null = null

    /** Запит у польоті — щоб десяток зірочок дав один запит, а не десяток. */
    private loading: Promise<void> | null = null

    constructor() {
        // Записи не змінюються на місці, лише замінюються — глибоко стежити нема за чим
        makeAutoObservable<this, 'loading'>(this, { items: observable.shallow, loading: false })
    }

    get isLoaded(): boolean {
        return this.loadedFor !== null && this.loadedFor === currentUser()
    }

    find(entityCode: string, entityUuid: string): Favorite | undefined {
        if (!this.isLoaded) return undefined

        return this.items.find((item) => item.entityCode === entityCode && item.entityUuid === entityUuid)
    }

    isPending(entityCode: string, entityUuid: string): boolean {
        return this.pending.includes(keyOf({ entityCode, entityUuid }))
    }

    /** Прочитати список, якщо для цього користувача ще не читали. */
    ensureLoaded(): Promise<void> {
        return this.isLoaded ? Promise.resolve() : this.load()
    }

    /** Перечитати список із сервера. */
    load(): Promise<void> {
        this.loading ??= this.fetch().finally(() => {
            this.loading = null
        })

        return this.loading
    }

    /**
     * Позначити запис, за потреби — з коментарем.
     *
     * Повторне позначення бек не дублює — віддає наявне; непорожній коментар
     * при цьому замінює наявний (`SPEC-014` §4.1). Тому запис, уже присутній в
     * обраному, з коментарем таки йде на сервер, а без коментаря — ні.
     */
    async add(entityCode: string, entityUuid: string, title: string, comment?: string): Promise<void> {
        const text = comment?.trim() || null
        const existing = this.find(entityCode, entityUuid)
        const draft: Favorite = { uuid: existing?.uuid ?? '', title, entityCode, entityUuid, comment: text ?? existing?.comment ?? null }
        const key = keyOf(draft)

        if (this.pending.includes(key) || (existing && !text)) return

        this.pending.push(key)
        this.items = [draft, ...this.items.filter((item) => keyOf(item) !== key)]

        try {
            const saved = (await restClient.post(FAVORITES_URL, {
                json: { title, entityCode, entityUuid, ...(text ? { comment: text } : {}) },
            })) as unknown as Favorite

            runInAction(() => {
                this.items = [
                    { ...draft, uuid: saved.uuid, comment: saved.comment ?? draft.comment ?? null },
                    ...this.items.filter((item) => keyOf(item) !== key),
                ]
            })
        } catch (error) {
            await this.settle(key, () => {
                const rest = this.items.filter((item) => keyOf(item) !== key)
                this.items = existing ? [existing, ...rest] : rest
            })

            // Помилка лише тоді, коли сервер справді не має того, що просили
            const saved = this.find(entityCode, entityUuid)
            if (!saved || (text && saved.comment !== text)) throw error
        } finally {
            this.release(key)
        }
    }

    /** Зняти позначку. Запис, якого на сервері вже немає, вважаємо знятим. */
    async remove(favorite: Favorite): Promise<void> {
        const key = keyOf(favorite)
        const index = this.items.findIndex((item) => item.uuid === favorite.uuid)

        if (this.pending.includes(key) || !favorite.uuid) return

        this.pending.push(key)
        this.items = this.items.filter((item) => item.uuid !== favorite.uuid)

        try {
            await restClient.delete(`${FAVORITES_URL}/${favorite.uuid}`)
        } catch (error) {
            await this.settle(key, () => {
                const items = this.items.filter((item) => keyOf(item) !== key)
                items.splice(Math.max(0, index), 0, favorite)
                this.items = items
            })

            if (this.find(favorite.entityCode, favorite.entityUuid)) throw error
        } finally {
            this.release(key)
        }
    }

    /**
     * «Видалити все» — усі свої позначки одним запитом (`DELETE /services/user/favorites`).
     *
     * Список очищується одразу; не вдалося — перечитуємо з сервера, і лишається
     * те, що там насправді.
     */
    async removeAll(): Promise<void> {
        const before = this.items

        this.items = []

        try {
            await restClient.delete(FAVORITES_URL)
        } catch (error) {
            runInAction(() => {
                this.items = before
            })

            try {
                await this.load()
            } catch (reloadError) {
                console.warn('FavoritesManager: не вдалося звірити обране з сервером', reloadError)
            }

            if (this.items.length > 0) throw error
        }
    }

    private async fetch(): Promise<void> {
        const user = currentUser()
        if (!user) return

        // Список іншого акаунта не показуємо навіть на мить
        if (this.loadedFor !== user) {
            this.items = []
            this.isLoading = true
        }

        try {
            const response = (await restClient.get(
                FAVORITES_URL,
                { searchParams: { limit: LIMIT } },
                NO_CACHE
            )) as unknown as FavoriteListResponse | null

            runInAction(() => {
                // Записи, які саме зараз позначають чи знімають, лишаються такими,
                // як їх бачить користувач: відповідь могла піти з сервера ще до
                // його запиту, і зірочка блимнула б назад
                const local = this.items.filter((item) => this.pending.includes(keyOf(item)))
                const remote = unique(response?.items ?? []).filter((item) => !this.pending.includes(keyOf(item)))

                this.items = [...local, ...remote]
                this.loadedFor = user
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    /**
     * Запит не вдався — звіряємося з сервером.
     *
     * Оптимістичну зміну відкочуємо й перечитуємо список: відповідь могла
     * загубитися вже після того, як бек усе зробив. Чи справді зміни немає,
     * вирішує викликач — за тим, що повернув сервер.
     */
    private async settle(key: string, revert: () => void): Promise<void> {
        runInAction(revert)
        this.release(key)

        try {
            await this.load()
        } catch (error) {
            console.warn('FavoritesManager: не вдалося звірити обране з сервером', error)
        }
    }

    private release(key: string): void {
        this.pending = this.pending.filter((item) => item !== key)
    }
}

/** Один запис на кожну сутність — навіть якщо в базі лишився дубль із давніших часів. */
const unique = (items: Favorite[]): Favorite[] => {
    const seen = new Set<string>()

    return items.filter((item) => {
        const key = keyOf(item)
        if (seen.has(key)) return false

        seen.add(key)

        return true
    })
}

export const favoritesManager = new FavoritesManager()
